// Result export: server-side streaming download (CSV / XLSX / SQL) + clipboard
// copy. The download is a two-step POST-spec → token → GET-stream so the browser
// can stream a file of any size to disk; clipboard variants read the same GET.
import { api } from './api.js';

// POST the spec → short-lived download token.
async function createToken(spec) {
  const r = await api.post('/api/export', spec);
  return r.token;
}

// Trigger a streaming file download via a one-shot <a> click.
//
// The `download` attribute is REQUIRED, and not just for the filename: the app
// uses @solidjs/router, whose global document click handler intercepts every
// same-origin <a> click and calls preventDefault() to do client-side routing —
// which would swallow the download. The router explicitly skips anchors that
// carry a `download` attribute (see its handleAnchorClick), so setting it both
// fixes the routing collision AND is the correct semantics for a download link.
export function triggerDownload(href, filename) {
  const a = document.createElement('a');
  a.href = href;
  a.download = filename; // also makes @solidjs/router ignore this click
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

// Trigger a streaming file download (csv | xlsx | sql). The server's
// Content-Disposition still carries the canonical name; the client name mirrors
// it so the download is sensibly named even before headers arrive.
export async function downloadExport(spec) {
  const token = await createToken(spec);
  const ext = { xlsx: 'xlsx', sql: 'sql', tsv: 'tsv', json: 'json', jsonl: 'jsonl', md: 'md' }[spec.format] || 'csv';
  const base = spec.kind === 'table' ? `${spec.database}.${spec.table}` : 'query-result';
  triggerDownload(`/api/export/${token}`, `${base}.${ext}`);
}

// Trigger a streaming database-backup download (.sql or .sql.gz). Same
// POST-spec → token → GET-stream shape as downloadExport, but the spec selects
// which object types to include rather than a result format. `spec` fields:
// { connectionId, database, data, triggers, routines, views, events,
//   stripDefiner, gzip, singleTransaction, note }.
// The server composes the canonical filename ({conn}-{db}-{timestamp}[-{note}])
// and sends it via Content-Disposition; the client name here is a fallback
// before headers arrive.
// Returns the job token so the caller can poll GET /api/progress/{token} (the
// streaming GET below publishes table-granular progress under the same token).
export async function downloadBackup(spec) {
  const r = await api.post('/api/backup', spec);
  triggerDownload(`/api/backup/${r.token}`, `${spec.database}-backup${spec.gzip ? '.sql.gz' : '.sql'}`);
  return r.token;
}

// Trigger a streaming SINGLE-TABLE dump download. `spec`:
//   { connectionId, database, table, mode, addDropTable, includeTriggers,
//     gzip, singleTransaction, stripDefiner }
// where mode ∈ "data-only" | "data+structure" | "structure-only".
// Returns the token (no progress UI today — the dump is usually small enough
// that the browser's own download UI suffices; add a progress modal if a big
// table makes that uncomfortable).
export async function downloadTableDump(spec) {
  const r = await api.post('/api/table-dump', spec);
  const ext = spec.gzip ? '.sql.gz' : '.sql';
  triggerDownload(`/api/table-dump/${r.token}`, `${spec.database}-${spec.table}${ext}`);
  return r.token;
}

// Copy the FULL result to the clipboard as TSV (server streams it; the browser
// must hold the text to put it on the clipboard, so this is bounded by browser
// memory — fine for up to a few hundred-k rows, not millions). Returns char count.
export async function copyFullToClipboard(spec) {
  const token = await createToken({ ...spec, format: 'tsv' });
  const res = await fetch(`/api/export/${token}`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`export failed (${res.status})`);
  const text = await res.text();
  await copyText(text);
  return text.length;
}

// Escape one value as a TSV field (quote + double inner quotes when it contains
// a tab/newline/quote). Shared by the clipboard copiers and the result grid.
export function escapeTSVField(v) {
  const s = v == null ? '' : String(v);
  return /[\t\n\r"]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

// Copy the rows ALREADY loaded in the grid as TSV — pure client-side, no server.
// columns: [{name,...}]; rows: any[][] in column order.
export async function copyRowsToClipboard(columns, rows) {
  const header = columns.map((c) => escapeTSVField(c.name)).join('\t');
  const body = rows.map((row) => row.map(escapeTSVField).join('\t')).join('\n');
  await copyText(rows.length ? header + '\n' + body : header);
  return rows.length;
}

// Clipboard write with an http-context fallback (navigator.clipboard needs a
// secure context; execCommand still works on plain http / LAN). Exported so the
// result grid can reuse the same fallback for copy-cell / copy-range / stats.
// readClipboardPrefill — best-effort read of a SHORT single text value from the
// clipboard, to prefill a column filter when the user clicks a header. Returns ''
// when there's no permission/secure context, or the clipboard holds something
// that isn't a single short value (≤254 chars, no tab/newline → not a copied
// row/column). Shared by the Browser data grid + the query-result grid.
export async function readClipboardPrefill() {
  try {
    if (navigator.clipboard?.readText) {
      const t = await navigator.clipboard.readText();
      if (t && t.length < 255 && !/[\t\r\n]/.test(t)) return t;
    }
  } catch { /* no permission / insecure context → no prefill */ }
  return '';
}

export async function copyText(text) {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.position = 'fixed';
  ta.style.top = '-1000px';
  ta.style.opacity = '0';
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  const ok = document.execCommand('copy');
  ta.remove();
  if (!ok) throw new Error('clipboard unavailable (try over HTTPS)');
}
